import { ButtonGroup } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { DogBaseSearch, DogBreederApi, DogBreederGridDto } from "../../../../../../api";
import { CustomDataTable, EmailButton, ExportButton } from "../../../../../../components";
import { useAuth, useGlobalData } from "../../../../../../contexts";
import { ERoutes } from "../../../../../../ERoutes";
import { useApiService, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { generateAddress } from "../../../../../../utils";

export interface IKennelOwnersListProps {
  dogId: number;
  editMode?: boolean;
}

export const DogKennelOwnersList: React.FunctionComponent<IKennelOwnersListProps> = ({ dogId, editMode = false }) => {
  const { t } = useTl();
  const api = useApiService(DogBreederApi);
  const { currentLanguage } = useGlobalData();
  const { userPermissions } = useAuth();
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5],
    pageSize: 5,
    sortKeys: { lastName: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogBaseSearch) => {
      const s = DogBaseSearch.fromJS({
        ...sObj,
        dogId: dogId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(s);
      return api.baseSearch(s);
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: DogBreederGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.computedName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.Email),
        fieldName: "email",
        render: (value: DogBreederGridDto) => <EmailButton email={value.email} />,
      },
      {
        header: () => t(ETLCodes.MailAddress),
        fieldName: "address",
        render: (row: DogBreederGridDto) =>
          generateAddress(row.number, row.street, row.city, row.postalCode, row.country),
      },
    ],
    [t]
  );

  return (
    <>
      <FieldSet
        title={t(ETLCodes.KennelOwners)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode &&
              userPermissions.some(
                (p) =>
                  p.module.toUpperCase() === "DOG" &&
                  p.permission.toUpperCase() === "E" &&
                  p.tab.toUpperCase() === "ADDBREEDER"
              )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
    </>
  );
};
